import moment from "moment-timezone";

import Assert from "tbAssert";

const mTransferFormat = new WeakMap();
const mPrettyFormat = new WeakMap();
const mPrettyNoTimeFormat = new WeakMap();

const mReportTimeZone = new WeakMap();

const mReportStart = new WeakMap();
const mReportEnd = new WeakMap();

const mPostMomentParseModificationFn = new WeakMap();

function formatMomentVal(momentVal, formatStr) {
    if (momentVal != null) {
        return momentVal.format(formatStr);
    }
    else {
        return null;
    }
}

class ReportBounds {
    // boundsInitializerFn (tz) => { start: Moment, end: Moment }
    constructor({
        initialTimeZone,
        transferFormat,
        prettyFormat,
        boundsInitializerFn,
        postMomentParseModifierFn = null,
        prettyNoTimeFormat = null
    }) {

        Assert.StrictAssert(initialTimeZone != null, "Assertion Failure: Missing Initial Time Zone Value");
        Assert.StrictAssert((moment.tz.zone(initialTimeZone) != null), "Assertion Failure: Initial Time Zone Value Is Invalid");

        Assert.StrictAssert(transferFormat != null, "Assertion Failure: Missing Transfer Format String");
        Assert.StrictAssert(prettyFormat != null, "Assertion Failure: Missing Pretty Format String");
        Assert.StrictAssert(boundsInitializerFn != null, "Assertion Failure: Missing Bounds Initializer Function");

        mReportStart.set(this, null);
        mReportEnd.set(this, null);

        mTransferFormat.set(this, transferFormat);
        mPrettyFormat.set(this, prettyFormat);
        mPrettyNoTimeFormat.set(this, prettyNoTimeFormat != null ? prettyNoTimeFormat : prettyFormat);

        const initialBounds = boundsInitializerFn(initialTimeZone);
        Assert.StrictAssert(moment.isMoment(initialBounds.start), "Assertion Failure: Initial Bounds Start Value Is Not A Moment");
        Assert.StrictAssert(moment.isMoment(initialBounds.end), "Assertion Failure: Initial Bounds End Value Is Not A Moment");

        mReportStart.set(this, initialBounds.start);
        mReportEnd.set(this, initialBounds.end);
        mReportTimeZone.set(this, initialTimeZone);
        this._showPrettyTime = true;
        this._showReportEnd = true;

        if (postMomentParseModifierFn != null) {
            mPostMomentParseModificationFn.set(this, postMomentParseModifierFn);
        }
        else {
            mPostMomentParseModificationFn.set(this, (m) => m); // if no post-parse modification func is set, use the no-op function.
        }

    }

    _parseStrVal(dateVal) {
        let builtVal = null;
        if (typeof dateVal === "string") {
            let rMoment = moment.tz(dateVal, mTransferFormat.get(this), mReportTimeZone.get(this));
            if (rMoment.isValid()) {
                builtVal = mPostMomentParseModificationFn.get(this)(rMoment);
            }
        }

        return builtVal;
    }

    get showPrettyTime() {
        return this._showPrettyTime;
    }

    set showPrettyTime(newVal) {
        this._showPrettyTime = !!newVal;
    }

    get showReportEnd() {
        return this._showReportEnd;
    }

    set showReportEnd(newVal) {
        this._showReportEnd = !!newVal;
    }

    get reportStart() {
        return mReportStart.get(this);
    }

    get reportStartVal() {
        return formatMomentVal(mReportStart.get(this), mTransferFormat.get(this));
    }

    set reportStartVal(startVal) {
        let parsedStartVal = this._parseStrVal(startVal);
        mReportStart.set(this, parsedStartVal);
    }

    get reportEnd() {
        return mReportEnd.get(this);
    }

    get reportEndVal() {
        return formatMomentVal(mReportEnd.get(this), mTransferFormat.get(this));
    }

    set reportEndVal(endVal) {
        let parsedEndVal = this._parseStrVal(endVal);
        mReportEnd.set(this, parsedEndVal);
    }

    get _currentPrintFormat() {
        return this._showPrettyTime ? mPrettyFormat.get(this) : mPrettyNoTimeFormat.get(this);
    }

    get printFormatStart() {
        return formatMomentVal(mReportStart.get(this), this._currentPrintFormat);
    }

    get printFormatEnd() {
        return formatMomentVal(mReportEnd.get(this), this._currentPrintFormat);
    }

    get reportTimeZone() {
        return mReportTimeZone.get(this);
    }

    set reportTimeZone(updatedTimeZone) {
        if (moment.tz.zone(updatedTimeZone) != null) {
            mReportStart.get(this).tz(updatedTimeZone);
            mReportEnd.get(this).tz(updatedTimeZone);
            mReportTimeZone.set(this, updatedTimeZone);
        }
        else {
            throw new Error("Cannot set new report time zone: zone is invalid");
        }
    }

    isValid() {
        return ((this.reportStart != null) &&
        (this.reportEnd != null) &&
        (this.reportStart <= this.reportEnd));
    }
}

export default ReportBounds;



//////////////////
// WEBPACK FOOTER
// ./src/views/trackerbase-reports/report-models/ReportBounds.js
// module id = NEEK
// module chunks = 1